// De qué producto salen las primeras piezas (ADR-2026-10-02-onboarding-v2-piezas-con-cumbre-y-tienda):
// la tienda conectada, que se ve llegar de a uno con cuántos faltan, o una foto del dispositivo.

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { getTnStatus, listTnProductsAll, type TnProduct } from '../../../lib/tiendaNube';
import { enGuion } from '../../../lib/onboardingMarca/guion';
import {
  cruzarCatalogo,
  estadoDeLaCarga,
  filtrarPorNombre,
  MS_MAXIMO_MIRANDO,
  pausaDelSondeo,
  textoDeLaCarga,
  leerLlegados,
  llegadosDelGuion,
  TIENDA_DEL_GUION,
  type CatalogoDeLaTienda,
  type EstadoDeLaCarga,
  type ProductoDeLaTienda,
} from '../../../lib/onboardingMarca/catalogoDeLaTienda';

const POLL_MS = 3_000;
/** Cuántos llegados se muestran antes de "Ver todos". */
const A_LA_VISTA = 12;
/** Cuántos lugares "esperando" se dibujan como mucho (el número dice el resto). */
const ESPERANDO_A_LA_VISTA = 3;

export type TiendaEnPiezas =
  | { estado: 'revisando' }
  | { estado: 'sin-tienda' }
  | { estado: 'conectada'; tienda: string | null; catalogo: CatalogoDeLaTienda; carga: EstadoDeLaCarga };

/**
 * La tienda conectada y lo que ya llegó de ella. Sólo mira si `activo` (respondió que vende por
 * Tiendanube); si no está conectada, o falla la consulta, es `sin-tienda` y queda la foto.
 */
export function useTiendaEnPiezas(p: { companyId: string | null; brandId: string | null; activo: boolean }): TiendaEnPiezas {
  const { companyId, brandId, activo } = p;
  const [estado, setEstado] = useState<TiendaEnPiezas>(activo ? { estado: 'revisando' } : { estado: 'sin-tienda' });

  useEffect(() => {
    if (!activo || !companyId || !brandId) {
      setEstado({ estado: 'sin-tienda' });
      return;
    }
    let vivo = true;
    let timer: number | undefined;
    const guion = enGuion();
    const abrio = Date.now();
    let ultimaNovedad = Date.now();
    let cuantosAntes = -1;

    const mirar = async (tienda: TnProduct[], nombreDeLaTienda: string | null) => {
      try {
        const llegados = guion
          ? llegadosDelGuion(Date.now() - abrio)
          : await leerLlegados(companyId, brandId, tienda.map((x) => x.id));
        if (!vivo) return;
        const catalogo = cruzarCatalogo(tienda, llegados);
        if (catalogo.llegados.length !== cuantosAntes) {
          cuantosAntes = catalogo.llegados.length;
          ultimaNovedad = Date.now();
        }
        const carga = estadoDeLaCarga({ faltan: catalogo.faltan, msSinNovedad: Date.now() - ultimaNovedad });
        setEstado({ estado: 'conectada', tienda: nombreDeLaTienda, catalogo, carga });
        // Se sigue mirando aunque se demore (más espaciado): con cientos de productos el import
        // tiene pausas largas entre tandas y lo que llega después tiene que aparecer igual.
        if (carga !== 'completo' && Date.now() - abrio < MS_MAXIMO_MIRANDO) {
          const pausa = guion ? 450 : pausaDelSondeo(Date.now() - abrio, carga);
          timer = window.setTimeout(() => void mirar(tienda, nombreDeLaTienda), pausa);
        }
      } catch {
        // Un tropiezo de red no corta la carga: se vuelve a mirar en la próxima vuelta.
        if (vivo) timer = window.setTimeout(() => void mirar(tienda, nombreDeLaTienda), POLL_MS * 2);
      }
    };

    void (async () => {
      if (guion) return mirar(TIENDA_DEL_GUION, 'Taller Norte');
      const status = await getTnStatus(brandId);
      if (!vivo) return;
      if (!status.connected) return setEstado({ estado: 'sin-tienda' });
      try {
        const tienda = await listTnProductsAll(brandId);
        if (!vivo) return;
        if (!tienda.length) return setEstado({ estado: 'sin-tienda' });
        await mirar(tienda, status.storeName);
      } catch {
        if (vivo) setEstado({ estado: 'sin-tienda' });
      }
    })();

    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, [activo, companyId, brandId]);

  return estado;
}

export interface FotoSubida {
  file: File;
  previewUrl: string;
}

/** Lo que eligió: un producto de la tienda (ya en Atlas) o la foto que subió. */
export type ProductoElegido = { origen: 'tienda'; producto: ProductoDeLaTienda } | { origen: 'foto' };

export function ElegirProducto({
  tienda,
  elegido,
  foto,
  nombreDeLaFoto,
  onElegir,
  onFoto,
  onNombreDeLaFoto,
}: {
  tienda: TiendaEnPiezas;
  elegido: ProductoElegido | null;
  foto: FotoSubida | null;
  nombreDeLaFoto: string;
  onElegir: (p: ProductoElegido) => void;
  onFoto: (e: ChangeEvent<HTMLInputElement>) => void;
  onNombreDeLaFoto: (nombre: string) => void;
}) {
  const inputFoto = useRef<HTMLInputElement>(null);
  const [todos, setTodos] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const conTienda = tienda.estado === 'conectada';
  const usaFoto = elegido?.origen === 'foto';

  const subir = (
    <>
      <button
        type="button"
        onClick={() => inputFoto.current?.click()}
        className={`om-subir ${usaFoto ? 'is-elegida' : ''} ${conTienda ? 'om-subir--chica' : ''}`}
        aria-pressed={usaFoto}
      >
        <span className="om-subir__foto">
          {foto ? (
            <img src={foto.previewUrl} alt="" />
          ) : (
            <span className="material-symbols-rounded" aria-hidden="true">
              add_photo_alternate
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="om-subir__titulo">
            {foto ? 'Cambiar la foto' : conTienda ? '¿No está? Sube una foto' : 'Subir una foto de tu producto'}
          </span>
          <span className="om-subir__detalle">El producto solo, bien iluminado. JPG o PNG.</span>
        </span>
      </button>
      <input ref={inputFoto} type="file" accept="image/*" className="hidden" onChange={onFoto} />
      {usaFoto && foto && (
        <label className="om-etiqueta">
          Nombre del producto
          <input
            id="om-producto"
            value={nombreDeLaFoto}
            onChange={(e) => onNombreDeLaFoto(e.target.value)}
            placeholder="Ej: Jean baggy cargo"
            className="om-input"
          />
        </label>
      )}
    </>
  );

  if (tienda.estado !== 'conectada') {
    return (
      <div className="flex flex-col gap-3">
        {tienda.estado === 'revisando' && (
          <p className="om-carga" role="status">
            <span className="om-lectura__punto om-pulso" aria-hidden="true" />
            Buscando tu tienda…
          </p>
        )}
        {subir}
      </div>
    );
  }

  const { catalogo, carga } = tienda;
  // Buscando, se ven todos los que coinciden; sin buscar, los primeros y "Ver todos".
  const buscando = busqueda.trim().length > 0;
  const coinciden = buscando ? filtrarPorNombre(catalogo.llegados, busqueda) : catalogo.llegados;
  const visibles = todos || buscando ? coinciden : coinciden.slice(0, A_LA_VISTA);
  const esperando = carga === 'cargando' && !buscando ? Math.min(catalogo.faltan, ESPERANDO_A_LA_VISTA) : 0;
  const elegidoTnId = elegido?.origen === 'tienda' ? elegido.producto.tnId : null;

  return (
    <div className="flex flex-col gap-3">
      <p className={`om-carga ${carga === 'completo' ? 'is-completa' : ''}`} role="status" aria-live="polite">
        {carga === 'cargando' ? (
          <span className="om-lectura__punto om-pulso" aria-hidden="true" />
        ) : (
          <span className="material-symbols-rounded om-carga__icono" aria-hidden="true">
            {carga === 'completo' ? 'check_circle' : 'schedule'}
          </span>
        )}
        <span>
          {textoDeLaCarga({
            llegados: catalogo.llegados.length,
            total: catalogo.total,
            faltan: catalogo.faltan,
            carga,
            tienda: tienda.tienda,
          })}
        </span>
      </p>
      {carga === 'cargando' && (
        <div className="om-carga__barra" aria-hidden="true">
          <span style={{ transform: `scaleX(${catalogo.total ? catalogo.llegados.length / catalogo.total : 0})` }} />
        </div>
      )}

      {catalogo.llegados.length > 0 && (
        <div className="om-busqueda">
          <span className="material-symbols-rounded om-busqueda__icono" aria-hidden="true">
            search
          </span>
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar entre tus productos"
            aria-label="Buscar entre tus productos por nombre"
            className="om-input om-busqueda__campo"
          />
          {buscando && (
            <button type="button" className="om-busqueda__borrar" onClick={() => setBusqueda('')} aria-label="Borrar la búsqueda">
              <span className="material-symbols-rounded" aria-hidden="true">
                close
              </span>
            </button>
          )}
        </div>
      )}
      {buscando && coinciden.length === 0 && (
        <p className="om-busqueda__vacio" role="status">
          Ningún producto coincide con “{busqueda.trim()}”. Prueba con otra palabra o sube una foto.
        </p>
      )}

      <ul className="om-productos" aria-label="Tus productos">
        {visibles.map((p) => {
          const es = elegidoTnId === p.tnId;
          return (
            <li key={p.tnId} className="om-producto__lugar">
              <button
                type="button"
                className={`om-producto ${es ? 'is-elegido' : ''}`}
                aria-pressed={es}
                onClick={() => onElegir({ origen: 'tienda', producto: p })}
              >
                <span className="om-producto__foto">
                  {p.imagen ? <img src={p.imagen} alt="" loading="lazy" /> : null}
                  {es && (
                    <span className="om-producto__tilde" aria-hidden="true">
                      <svg viewBox="0 0 20 20" className="h-[14px] w-[14px]">
                        <path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" strokeWidth="2.9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  )}
                </span>
                <span className="om-producto__nombre">{p.nombre}</span>
              </button>
            </li>
          );
        })}
        {Array.from({ length: esperando }, (_, i) => (
          <li key={`esperando-${i}`} className="om-producto__lugar" aria-hidden="true">
            <span className="om-producto is-esperando">
              <span className="om-producto__foto om-esqueleto" />
              <span className="om-producto__nombre">&nbsp;</span>
            </span>
          </li>
        ))}
      </ul>
      {!buscando && catalogo.llegados.length > A_LA_VISTA && (
        <button type="button" className="om-seccion__boton self-start" onClick={() => setTodos((t) => !t)}>
          {todos ? 'Ver menos' : `Ver los ${catalogo.llegados.length}`}
        </button>
      )}

      {subir}
    </div>
  );
}

export default ElegirProducto;
